import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Reveal } from "@/components/Reveal";
import { MapPin, Users, Ticket, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Events() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const load = () => api.get("/events").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const rsvp = async (id) => {
    if (!user) { toast.error("Sign in to RSVP"); return; }
    try { const { data } = await api.post(`/events/${id}/rsvp`); setItems((prev) => prev.map((e) => e.id === id ? { ...e, attendees: data.attendees } : e)); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const buyTicket = async (id) => {
    if (!user) { toast.error("Sign in to get tickets"); return; }
    try { const { data } = await api.post(`/events/${id}/ticket/checkout`, { origin_url: window.location.origin, quantity: 1 }); window.location.href = data.checkout_url; }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const going = (e) => user && (e.attendees || []).some((a) => a.id === user.id);

  return (
    <div className="min-h-screen bg-bone pt-20 md:pt-14 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-black">Gather Together</span>
          <h1 className="font-serif font-light text-5xl md:text-7xl tracking-tight mt-4">Events</h1>
        </Reveal>

        <div className="mt-16 space-y-px bg-charcoal/10 border border-charcoal/10">
          {items.map((e, i) => (
            <Reveal key={e.id} delay={(i % 4) * 0.06} className="bg-bone">
              <div className="group grid grid-cols-1 md:grid-cols-12 gap-6 p-8 hover:bg-sand transition-colors duration-400" data-testid={`event-${e.id}`}>
                <div className="md:col-span-2">
                  <span className="font-serif text-5xl leading-none">{new Date(e.date).getDate()}</span>
                  <span className="font-mono text-xs uppercase tracking-widest text-charcoal/50 block mt-1">
                    {new Date(e.date).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                  </span>
                </div>
                <div className="md:col-span-7">
                  <h3 className="font-serif text-3xl tracking-tight">{e.title}</h3>
                  <p className="font-sans text-charcoal/60 mt-2 max-w-xl">{e.description}</p>
                  <div className="flex items-center gap-6 mt-4 font-mono text-xs uppercase tracking-widest text-charcoal/50 flex-wrap">
                    <span className="flex items-center gap-2"><MapPin size={14} /> {e.location}</span>
                    <span className="flex items-center gap-2"><Users size={14} /> {(e.attendees || []).length} going</span>
                    {e.ticketed && e.price > 0 && <span className="flex items-center gap-2 text-gold"><Ticket size={14} /> ${e.price} ticket</span>}
                    {e.early_access && <span className="flex items-center gap-2 text-navy"><Lock size={13} /> Early Access · Gold+</span>}
                  </div>
                </div>
                <div className="md:col-span-3 flex md:justify-end items-start">
                  {e.ticketed && e.price > 0 ? (
                    <button onClick={() => buyTicket(e.id)} data-testid={`ticket-${e.id}`}
                      className="px-6 py-3 font-mono text-xs uppercase tracking-widest bg-navy text-bone hover:bg-charcoal transition-colors duration-300">
                      Get Tickets · ${e.price}
                    </button>
                  ) : (
                    <button onClick={() => rsvp(e.id)} data-testid={`rsvp-${e.id}`}
                      className={`px-6 py-3 font-mono text-xs uppercase tracking-widest transition-colors duration-300 ${going(e) ? "bg-navy text-bone" : "border border-charcoal/30 hover:bg-charcoal hover:text-bone"}`}>
                      {going(e) ? "You're Going ✓" : "RSVP"}
                    </button>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {user?.role === "admin" && <AdminCreate onCreated={load} />}
      </div>
    </div>
  );
}

function AdminCreate({ onCreated }) {
  const [f, setF] = useState({ title: "", description: "", date: "", location: "", image: "https://images.unsplash.com/photo-1517456793572-1d8efd6dc135", ticketed: false, price: "", capacity: "", early_access: false });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/events", { ...f, price: Number(f.price) || 0, capacity: Number(f.capacity) || 0 });
      setF({ ...f, title: "", description: "", date: "", location: "", ticketed: false, price: "", capacity: "", early_access: false });
      onCreated();
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-24 border-t border-charcoal/20 pt-12">
      <h2 className="font-serif text-3xl mb-8">New Event</h2>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="admin-event-form">
        <input placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required data-testid="evt-title" className="border-b border-charcoal/30 bg-transparent py-3 font-sans focus:outline-none focus:border-navy" />
        <input placeholder="Location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="evt-location" className="border-b border-charcoal/30 bg-transparent py-3 font-sans focus:outline-none focus:border-navy" />
        <input type="datetime-local" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} required data-testid="evt-date" className="border-b border-charcoal/30 bg-transparent py-3 font-sans focus:outline-none focus:border-navy" />
        <input placeholder="Image URL" value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} className="border-b border-charcoal/30 bg-transparent py-3 font-sans focus:outline-none focus:border-navy" />
        <textarea placeholder="Description" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} required data-testid="evt-desc" className="md:col-span-2 border-b border-charcoal/30 bg-transparent py-3 font-sans focus:outline-none focus:border-navy resize-none" />
        <div className="md:col-span-2 border border-charcoal/10 p-6">
          <label className="flex items-center gap-3 cursor-pointer" data-testid="evt-ticketed-toggle">
            <input type="checkbox" checked={f.ticketed} onChange={(e) => setF({ ...f, ticketed: e.target.checked })} className="accent-navy w-4 h-4" />
            <span className="font-mono text-xs uppercase tracking-widest text-charcoal/70">This is a ticketed (paid) event</span>
          </label>
          {f.ticketed && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
              <div>
                <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Ticket Price (USD)</label>
                <input type="number" min="1" step="1" placeholder="45" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="evt-price" className="w-full border-b border-charcoal/30 bg-transparent py-3 font-sans focus:outline-none focus:border-navy" />
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Capacity (0 = unlimited)</label>
                <input type="number" min="0" placeholder="100" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} data-testid="evt-capacity" className="w-full border-b border-charcoal/30 bg-transparent py-3 font-sans focus:outline-none focus:border-navy" />
              </div>
            </div>
          )}
        </div>
        <label className="md:col-span-2 flex items-center gap-3 cursor-pointer border border-charcoal/10 p-6" data-testid="evt-early-toggle">
          <input type="checkbox" checked={f.early_access} onChange={(e) => setF({ ...f, early_access: e.target.checked })} className="accent-navy w-4 h-4" />
          <span className="font-mono text-xs uppercase tracking-widest text-charcoal/70">Early access — Gold & Platinum supporters only</span>
        </label>
        <button disabled={busy} data-testid="evt-submit" className="md:col-span-2 bg-charcoal text-bone py-4 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-50">{busy ? "Creating..." : "Create Event"}</button>
      </form>
    </div>
  );
}
